import { Component, Suspense, lazy, type ComponentType, type FC } from "react";
import { Alert, Button, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { captureError } from "@/shared/utils/apm";
import { reloadWithoutPrompt } from "@/shared/utils/reloadWithoutPrompt";
import { DialogHeader } from "@/shared/components";
import { isChunkLoadError } from "../model/loadFailure";
import type { AutomationEditorProps } from "./AutomationEditor";
import { AutomationDialogLoadingContent } from "./AutomationDialogLoading";
import { loadAutomationEditor } from "./loadAutomationEditor";

const LoadFailed: FC<{ onClose: () => void; onRetry: () => void }> = ({
  onClose,
  onRetry,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <DialogHeader title={t("automations.editor.title")} onClose={onClose} />
      <DialogContent>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              {t("automations.list.retry")}
            </Button>
          }
        >
          {t("automations.editor.loadFailed")}
        </Alert>
      </DialogContent>
    </>
  );
};

/**
 * Настоящий баг в коде редактора (не отказ загрузки чанка): показываем
 * нейтральный текст вместо «не удалось загрузить, проверьте соединение»
 * (это про сеть, а не про баг) и кнопку обновления страницы вместо
 * повтора того же рендера, который снова упадёт тем же исключением.
 */
const Crashed: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  return (
    <>
      <DialogHeader title={t("automations.editor.title")} onClose={onClose} />
      <DialogContent>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => reloadWithoutPrompt()}
            >
              {t("automations.editor.reload")}
            </Button>
          }
        >
          {t("automations.editor.crashed")}
        </Alert>
      </DialogContent>
    </>
  );
};

// Экспортирован только для теста: приводит возврат getDerivedStateFromError
// (Pick<State, "status">) к точному члену объединения при ручной подстановке
// состояния без прохода через реальный жизненный цикл React.
export type State =
  | { status: "ok"; Lazy: ComponentType<AutomationEditorProps> }
  | { status: "chunkFailed" }
  | { status: "crashed" };

/**
 * Ловит и отказ ленивого `import("./AutomationEditor")` (не загрузился
 * чанк, например старый чанк удалён с хостинга после выката), и настоящие
 * баги в дереве редактора (холст, панели, статистика): различает их по
 * `isChunkLoadError` (Review, фикс-раунд 2), а не показывает оба случая
 * одним текстом «не удалось загрузить, проверьте соединение»: так было
 * до этого раунда, и это маскировало реальные баги под сетевую проблему,
 * а сами баги никуда не репортились.
 *
 * Отказ загрузки чанка: тот же текст, что раньше, но кнопка «Повторить»
 * теперь по-настоящему повторяет попытку. `React.lazy` кеширует отказавший
 * промис своей ленивой обёртки навсегда: повторный вызов `import(...)`
 * без новой обёртки `lazy(...)` ничего не даст, React отрисует тот же
 * отказ снова. Поэтому `retry` создаёт новый `lazy(loadAutomationEditor)`
 * и кладёт его в state: другая ссылка на компонент, другой `import()`.
 *
 * Настоящий баг: репортим через тот же канал, что и верхнеуровневая
 * `ErrorMonitoring` (`captureError` из `@/shared/utils/apm`, тот же
 * `apm?.captureError`), и показываем отдельный текст с кнопкой
 * перезагрузки страницы (тот же рендер тут же упал бы снова, повторять
 * нечего). Диалог остаётся закрываемым в обоих состояниях.
 */
export class AutomationEditorErrorBoundary extends Component<
  AutomationEditorProps,
  State
> {
  state: State = { status: "ok", Lazy: lazy(loadAutomationEditor) };

  static getDerivedStateFromError(error: unknown): Pick<State, "status"> {
    return { status: isChunkLoadError(error) ? "chunkFailed" : "crashed" };
  }

  componentDidCatch(error: unknown): void {
    if (!isChunkLoadError(error)) {
      captureError(error, "AutomationEditorErrorBoundary");
    }
  }

  private retry = (): void => {
    this.setState({ status: "ok", Lazy: lazy(loadAutomationEditor) });
  };

  render() {
    const { onClose } = this.props;
    if (this.state.status === "chunkFailed") {
      return <LoadFailed onClose={onClose} onRetry={this.retry} />;
    }
    if (this.state.status === "crashed") {
      return <Crashed onClose={onClose} />;
    }
    const { Lazy } = this.state;
    return (
      <Suspense fallback={<AutomationDialogLoadingContent onClose={onClose} />}>
        <Lazy {...this.props} />
      </Suspense>
    );
  }
}
